const OAUTH_PROVIDERS = {
    APPLE: 'apple',
    FACEBOOK: 'facebook',
    GOOGLE: 'google',
} as const;

type OAuthProvider = (typeof OAUTH_PROVIDERS)[keyof typeof OAUTH_PROVIDERS];

const OAUTH_PROVIDER_NAMES: Record<OAuthProvider, string> = {
    [OAUTH_PROVIDERS.APPLE]: 'Apple',
    [OAUTH_PROVIDERS.FACEBOOK]: 'Facebook',
    [OAUTH_PROVIDERS.GOOGLE]: 'Google',
};

type OAuthRequest = {
    state: string;
    nonce?: string;
};

type ConsumedOAuthRequest = {
    expectedNonce?: string;
};

export function beginOAuthRequest(provider: OAuthProvider, requiresNonce: boolean): OAuthRequest {
    const state = createOAuthRequestValue();
    const nonce = requiresNonce ? createOAuthRequestValue() : undefined;
    try {
        sessionStorage.setItem(stateKey(provider), state);
        if (nonce !== undefined) sessionStorage.setItem(nonceKey(provider), nonce);
    } catch {
        clearStoredRequestBestEffort(provider);
        throw new Error(`${providerName(provider)} OAuth session storage unavailable`);
    }
    return nonce === undefined ? { state } : { state, nonce };
}

function createOAuthRequestValue(): string {
    if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function')
        throw new Error('Secure random values are unavailable');

    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map((value) => value.toString(16).padStart(2, '0'))
        .join('');
}

export function consumeOAuthRequest(
    provider: OAuthProvider,
    returnedState: string | null,
    requiresNonce: boolean
): ConsumedOAuthRequest {
    let expectedState: string | null;
    let expectedNonce: string | null;
    try {
        expectedState = sessionStorage.getItem(stateKey(provider));
        expectedNonce = sessionStorage.getItem(nonceKey(provider));
    } catch {
        clearStoredRequestBestEffort(provider);
        throw new Error(`${providerName(provider)} OAuth session storage unavailable`);
    }
    clearStoredRequest(provider);

    if (expectedState === null || returnedState !== expectedState)
        throw new Error(`${providerName(provider)} OAuth state mismatch`);
    if (requiresNonce && expectedNonce === null) throw new Error(`${providerName(provider)} OAuth nonce missing`);
    return expectedNonce === null ? {} : { expectedNonce };
}

export function validateOAuthNonce(
    provider: OAuthProvider,
    expectedNonce: string,
    returnedNonce: string | undefined
): void {
    if (returnedNonce !== expectedNonce) throw new Error(`${providerName(provider)} OAuth nonce mismatch`);
}

const stateKey = (provider: OAuthProvider): string => `${provider}_oauth_state`;

const nonceKey = (provider: OAuthProvider): string => `${provider}_oauth_nonce`;

const providerName = (provider: OAuthProvider): string => OAUTH_PROVIDER_NAMES[provider];

const clearStoredRequest = (provider: OAuthProvider): void => {
    let removalFailed = false;
    for (const key of [stateKey(provider), nonceKey(provider)]) {
        try {
            sessionStorage.removeItem(key);
        } catch {
            removalFailed = true;
        }
    }
    if (removalFailed) throw new Error(`${providerName(provider)} OAuth session storage unavailable`);
};

const clearStoredRequestBestEffort = (provider: OAuthProvider): void => {
    try {
        sessionStorage.removeItem(stateKey(provider));
        sessionStorage.removeItem(nonceKey(provider));
    } catch {
        // The request cannot be reused when storage is unavailable.
    }
};

export { OAUTH_PROVIDERS };
